/**
 * Admin wizard detail page for /admin/wizards/:wizardId route.
 * Shows wizard template details, steps editor, handle editing, entries,
 * and a panel to create wizard instances for users.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { WizardEntriesList } from "../components/admin/WizardEntriesList";
import { WizardStepsEditor } from "../components/admin/WizardStepsEditor";
import { Navigate, useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { adminRoutes, publicRoutes } from "../lib/routes";
import {
  adminGetAllUsers,
  adminGetSettings,
  createWizardInstance,
  getWizardTemplate,
  updateWizardTemplate,
  sendWizardInvite,
  type AdminUser,
  type WizardState,
} from "../services/naraDoor";
import {
  Check,
  Search,
  Copy,
  Loader2,
  User,
  PlayCircle,
  ExternalLink,
  Eye,
  HelpCircle,
  Pencil,
  Save,
  X,
  Info,
  AlertTriangle,
  Mail,
} from "lucide-react";

export function AdminWizardDetail() {
  const { t } = useTranslation();
  const { wizardId } = useParams<{ wizardId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isProfileLoading, profile } = useProfile();

  // Fetch template via useQuery
  const { data: template, isLoading: isTemplateLoading } = useQuery({
    queryKey: ["wizardTemplate", wizardId],
    queryFn: () => getWizardTemplate(wizardId!),
    enabled: isAdmin && !!wizardId,
  });

  // Create Instance state
  const [instanceSearchQuery, setInstanceSearchQuery] = useState("");
  const [instanceSearchResults, setInstanceSearchResults] = useState<AdminUser[]>([]);
  const [showInstanceDropdown, setShowInstanceDropdown] = useState(false);
  const [isInstanceSearching, setIsInstanceSearching] = useState(false);
  const [instanceUser, setInstanceUser] = useState<AdminUser | null>(null);
  const [instanceStatus, setInstanceStatus] = useState<"idle" | "creating" | "success" | "error">("idle");
  const [createdInstance, setCreatedInstance] = useState<WizardState | null>(null);
  const [instanceError, setInstanceError] = useState<string | null>(null);

  // Send invite state
  const [inviteStatus, setInviteStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<"overview" | "entries">("overview");

  // Description editing state
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState("");

  // Slug editing state (danger zone)
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugValue, setSlugValue] = useState("");
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [slugSaved, setSlugSaved] = useState(false);
  const [showSlugEditor, setShowSlugEditor] = useState(false);

  const instanceSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const instanceDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (instanceDropdownRef.current && !instanceDropdownRef.current.contains(e.target as Node)) {
        setShowInstanceDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Instance user search (debounced)
  const handleInstanceSearchChange = useCallback((query: string) => {
    setInstanceSearchQuery(query);
    if (instanceSearchTimerRef.current) clearTimeout(instanceSearchTimerRef.current);
    if (!query.trim()) {
      setInstanceSearchResults([]);
      setShowInstanceDropdown(false);
      return;
    }
    instanceSearchTimerRef.current = setTimeout(async () => {
      setIsInstanceSearching(true);
      try {
        const res = await adminGetAllUsers(0, 10, query.trim());
        setInstanceSearchResults(res.users);
        setShowInstanceDropdown(true);
      } catch {
        setInstanceSearchResults([]);
      } finally {
        setIsInstanceSearching(false);
      }
    }, 300);
  }, []);

  const selectInstanceUser = (user: AdminUser) => {
    setInstanceUser(user);
    setInstanceSearchQuery("");
    setShowInstanceDropdown(false);
    setCreatedInstance(null);
    setInstanceStatus("idle");
    setInstanceError(null);
    setInviteStatus("idle");
    setInviteError(null);
  };

  const handleCreateInstance = async () => {
    if (!instanceUser || !wizardId) return;
    setInstanceStatus("creating");
    setInstanceError(null);
    try {
      const instance = await createWizardInstance(wizardId, instanceUser.id);
      setCreatedInstance(instance);
      setInstanceStatus("success");
    } catch (err) {
      setInstanceError(err instanceof Error ? err.message : String(err));
      setInstanceStatus("error");
    }
  };

  const handleSendInvite = async () => {
    if (!instanceUser || !createdInstance) return;
    setInviteStatus("sending");
    setInviteError(null);

    try {
      // Load email templates from admin settings
      const DEFAULT_TEMPLATES = {
        emailSubject: "{{ sender.name }} invited you to complete a step",
        emailTemplate:
          'Hi {{ recipient.name }},\n\n{{ sender.name }} would like you to complete the "{{ step_name }}" step.\n\nClick here to continue: {{ wizard_link }}\n\nBest regards,\nThe Nara Team',
      };

      let emailSubject = DEFAULT_TEMPLATES.emailSubject;
      let emailTemplate = DEFAULT_TEMPLATES.emailTemplate;

      try {
        const settings = await adminGetSettings();
        if (settings?.wizardInviteTemplates) {
          if (settings.wizardInviteTemplates.emailSubject) emailSubject = settings.wizardInviteTemplates.emailSubject;
          if (settings.wizardInviteTemplates.emailTemplate) emailTemplate = settings.wizardInviteTemplates.emailTemplate;
        }
      } catch {
        // Use defaults if settings fetch fails
      }

      const recipientName = [instanceUser.first_name, instanceUser.last_name].filter(Boolean).join(" ") || "there";
      const senderName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || "Admin";
      const wizardLink = `${window.location.origin}/wizard/${createdInstance.instance_id}`;
      const stepName = template?.name || "wizard";

      const renderTemplate = (tmpl: string) =>
        tmpl
          .replace(/\{\{\s*recipient\.name\s*\}\}/g, recipientName)
          .replace(/\{\{\s*sender\.name\s*\}\}/g, senderName)
          .replace(/\{\{\s*wizard_link\s*\}\}/g, wizardLink)
          .replace(/\{\{\s*step_name\s*\}\}/g, stepName);

      const renderedMessage = renderTemplate(emailTemplate);

      await sendWizardInvite({
        recipientUserId: instanceUser.id,
        channel: "email",
        message: renderedMessage,
        wizardLink,
        recipientEmail: instanceUser.email || undefined,
      });

      setInviteStatus("sent");
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : String(err));
      setInviteStatus("error");
    }
  };

  const handleTitleSave = async (newTitle: string) => {
    if (!wizardId || !newTitle.trim()) return;
    await updateWizardTemplate(wizardId, { name: newTitle.trim() });
    queryClient.invalidateQueries({ queryKey: ["wizardTemplate", wizardId] });
    queryClient.invalidateQueries({ queryKey: ["wizardTemplates"] });
  };

  const handleDescriptionSave = async () => {
    if (!wizardId) return;
    try {
      await updateWizardTemplate(wizardId, { description: descriptionValue.trim() });
      queryClient.invalidateQueries({ queryKey: ["wizardTemplate", wizardId] });
      setEditingDescription(false);
    } catch {
      // Keep editing state open on error
    }
  };

  const handleToggleActive = async () => {
    if (!wizardId || !template) return;
    await updateWizardTemplate(wizardId, { is_active: !template.is_active });
    queryClient.invalidateQueries({ queryKey: ["wizardTemplate", wizardId] });
    queryClient.invalidateQueries({ queryKey: ["wizardTemplates"] });
  };

  const handleSaveSlug = async () => {
    if (!wizardId || !slugValue.trim()) return;
    setSlugSaving(true);
    setSlugError(null);
    setSlugSaved(false);
    try {
      await updateWizardTemplate(wizardId, { slug: slugValue.trim() });
      queryClient.invalidateQueries({ queryKey: ["wizardTemplate", wizardId] });
      setEditingSlug(false);
      setShowSlugEditor(false);
      setSlugSaved(true);
      setTimeout(() => setSlugSaved(false), 3000);
    } catch (err) {
      setSlugError(err instanceof Error ? err.message : String(err));
    } finally {
      setSlugSaving(false);
    }
  };

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  if (!template && !isTemplateLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Wizard not found</p>
        <button
          onClick={() => navigate(adminRoutes.wizards)}
          className="mt-4 text-sm text-nara-orange hover:underline"
        >
          {t("adminWizards.detail.back")}
        </button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={template?.name || "Wizard"}
        description={template?.description || ""}
        isAdmin
        breadcrumbs={[
          { label: t("adminWizards.breadcrumbs.dashboard"), href: publicRoutes.home },
          { label: t("adminWizards.breadcrumbs.admin") },
          { label: t("adminWizards.breadcrumbs.wizards"), href: adminRoutes.wizards },
          { label: template?.name || wizardId || "..." },
        ]}
        onTitleSave={handleTitleSave}
        actions={
          template ? (
            <div className="flex items-center gap-2">
              <Link
                to={adminRoutes.wizardHelp}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                title="Help"
              >
                <HelpCircle className="h-4 w-4" />
              </Link>
              <a
                href={`/wizard/s/${template.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                <Eye className="h-4 w-4" />
                Preview
              </a>
            </div>
          ) : undefined
        }
      />

      {/* Tab bar */}
      <div className="flex border-b border-border mb-6">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "overview"
              ? "border-nara-orange text-nara-orange"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("entries")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "entries"
              ? "border-nara-orange text-nara-orange"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Entries
        </button>
      </div>

      {activeTab === "overview" && (
      <div className="space-y-6">
        {isTemplateLoading && (
          <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("adminWizards.detail.loadingTemplate", { defaultValue: "Loading template..." })}
          </div>
        )}

        {/* 1. Versioning info banner */}
        {template && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              Changes to this template won&apos;t affect existing wizard instances. Each instance uses a frozen copy from when it was created.
            </div>
          </div>
        )}

        {/* 2. Template Info (with editable description + active toggle) */}
        {template && (
          <div className="bg-card border border-border rounded-xl p-5">
            <h4 className="text-sm font-semibold text-foreground mb-3">Template Info</h4>
            <div className="space-y-3">
              {/* Status toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <button
                  onClick={handleToggleActive}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-nara-orange/50 focus:ring-offset-2 ${
                    template.is_active ? "bg-green-500" : "bg-muted-foreground/30"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      template.is_active ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                  <span className="sr-only">{template.is_active ? "Active" : "Inactive"}</span>
                </button>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status label</span>
                <span className={template.is_active ? "text-green-600 font-medium" : "text-muted-foreground"}>
                  {template.is_active ? "Active" : "Inactive"}
                </span>
              </div>

              {/* Steps count */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Steps</span>
                <span>{template.steps.length}</span>
              </div>

              {/* Description (editable) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Description</span>
                  {!editingDescription && (
                    <button
                      onClick={() => {
                        setDescriptionValue(template.description || "");
                        setEditingDescription(true);
                      }}
                      className="p-1 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {editingDescription ? (
                  <div className="space-y-2">
                    <textarea
                      value={descriptionValue}
                      onChange={(e) => setDescriptionValue(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50 resize-none"
                      placeholder="Optional description..."
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleDescriptionSave}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors"
                      >
                        <Save className="h-3.5 w-3.5" />
                        Save
                      </button>
                      <button
                        onClick={() => setEditingDescription(false)}
                        className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-foreground">
                    {template.description || <span className="text-muted-foreground italic">No description</span>}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 3. Steps Editor */}
        {template && (
          <WizardStepsEditor
            templateId={template.id}
            steps={template.steps}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ["wizardTemplate", wizardId] });
              queryClient.invalidateQueries({ queryKey: ["wizardTemplates"] });
            }}
          />
        )}

        {/* 4. Create Instance Panel */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h4 className="text-sm font-semibold text-foreground mb-4">
            {t("adminWizards.detail.createInstanceTitle", { defaultValue: "Create Wizard Instance" })}
          </h4>

          {/* User search for instance */}
          <div className="mb-4 relative" ref={instanceDropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={instanceSearchQuery}
                onChange={(e) => handleInstanceSearchChange(e.target.value)}
                placeholder={t("adminWizards.detail.searchUsersForInstance", { defaultValue: "Search for a user..." })}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
              />
              {isInstanceSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
              )}
            </div>

            {/* Search dropdown */}
            {showInstanceDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
                {instanceSearchResults.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    {isInstanceSearching ? t("adminWizards.detail.searching") : t("adminWizards.detail.noResults")}
                  </div>
                ) : (
                  instanceSearchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => selectInstanceUser(user)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center gap-3"
                    >
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {[user.first_name, user.last_name].filter(Boolean).join(" ") || "Unnamed"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email || user.phone || "No contact info"}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Selected user + create button */}
          {instanceUser && (
            <div className="bg-muted/30 border border-border rounded-lg p-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-9 w-9 rounded-full bg-nara-orange/10 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-nara-orange" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">
                    {[instanceUser.first_name, instanceUser.last_name].filter(Boolean).join(" ") || "Unnamed"}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {instanceUser.email || instanceUser.phone || instanceUser.id}
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateInstance}
                disabled={instanceStatus === "creating"}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-nara-orange hover:bg-nara-orange/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {instanceStatus === "creating" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <PlayCircle className="h-4 w-4" />
                )}
                {instanceStatus === "creating"
                  ? t("adminWizards.detail.creatingInstance", { defaultValue: "Creating..." })
                  : t("adminWizards.detail.createInstance", { defaultValue: "Create Instance" })}
              </button>

              {/* Success: show link */}
              {instanceStatus === "success" && createdInstance && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 mb-2">
                    <Check className="h-4 w-4" />
                    <span className="font-medium">
                      {t("adminWizards.detail.instanceCreated", { defaultValue: "Instance created" })}
                    </span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {createdInstance.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono flex-1 truncate bg-muted px-2 py-1 rounded">
                      {`${window.location.origin}/wizard/${createdInstance.instance_id}`}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/wizard/${createdInstance.instance_id}`
                        );
                      }}
                      className="text-xs text-nara-orange hover:underline shrink-0"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <a
                      href={adminRoutes.wizardEntry(createdInstance.instance_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-nara-orange hover:underline shrink-0"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>

                  {/* Send Email Invite button */}
                  {instanceUser?.email && (
                    <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                      <button
                        onClick={handleSendInvite}
                        disabled={inviteStatus === "sending" || inviteStatus === "sent"}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {inviteStatus === "sending" ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : inviteStatus === "sent" ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Mail className="h-3.5 w-3.5" />
                        )}
                        {inviteStatus === "sending"
                          ? "Sending..."
                          : inviteStatus === "sent"
                            ? "Invite Sent"
                            : `Send Email Invite to ${instanceUser.email}`}
                      </button>
                      {inviteStatus === "error" && inviteError && (
                        <p className="mt-2 text-xs text-destructive">{inviteError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {instanceStatus === "error" && instanceError && (
                <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                  {instanceError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 5. Danger Zone (Slug + Template ID) */}
        {template && (
          <div className="bg-card border border-red-200 dark:border-red-800 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-destructive mb-1">Danger Zone</h4>
            <p className="text-xs text-muted-foreground mb-4">
              Actions here can break existing URLs or integrations.
            </p>

            {/* Handle / Slug */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-foreground">Handle (slug)</div>
                  <p className="text-xs text-muted-foreground">
                    Used in customer-facing URLs: <code className="bg-muted px-1.5 py-0.5 rounded text-xs">/wizard/{template.slug}</code>
                  </p>
                </div>
                {!showSlugEditor && (
                  <button
                    onClick={() => {
                      setShowSlugEditor(true);
                      setEditingSlug(true);
                      setSlugValue(template.slug);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 border border-destructive/30 rounded-lg transition-colors"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Edit Handle
                  </button>
                )}
              </div>

              {showSlugEditor && editingSlug && (
                <div className="bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-3">
                  <p className="text-xs text-destructive flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Changing the handle breaks existing URLs using this slug.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={slugValue}
                      onChange={(e) => setSlugValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                      className="flex-1 px-3 py-2 text-sm font-mono border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
                      placeholder="my-wizard-handle"
                    />
                    <button
                      onClick={handleSaveSlug}
                      disabled={slugSaving || !slugValue.trim() || slugValue === template.slug}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-destructive hover:bg-destructive/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {slugSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowSlugEditor(false);
                        setEditingSlug(false);
                        setSlugValue(template.slug);
                        setSlugError(null);
                      }}
                      className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {slugError && (
                    <p className="text-sm text-destructive">{slugError}</p>
                  )}
                </div>
              )}
              {slugSaved && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="h-4 w-4" />
                  Handle saved
                </p>
              )}

              {/* Template ID (read-only) */}
              <div className="pt-3 border-t border-border/50">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Template ID:</span>
                  <code className="bg-muted px-1.5 py-0.5 rounded font-mono">{template.id}</code>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {activeTab === "entries" && template && (
        <WizardEntriesList templateId={template.id} showUserColumn />
      )}

      {activeTab === "entries" && !template && !isTemplateLoading && (
        <p className="text-sm text-muted-foreground">No template loaded — cannot show entries.</p>
      )}
    </div>
  );
}
